import React from "react";
import { useLocation, useNavigationType } from "react-router-dom";

import scrollPageToPosition, {
  getPageScrollPosition,
  PageScrollPosition,
} from "../../services/scrollPageToPosition";
import scrollPageToTop from "../../services/scrollPageToTop";

interface ScrollRestorationLocationState {
  restoreScrollFromKey?: string;
}

const MAX_STORED_POSITIONS = 100;
const storedPositions = new Map<string, PageScrollPosition>();

const storePosition = (locationKey: string) => {
  storedPositions.delete(locationKey);
  storedPositions.set(locationKey, getPageScrollPosition());

  const oldestKey = storedPositions.keys().next().value as string | undefined;
  if (storedPositions.size > MAX_STORED_POSITIONS && oldestKey) {
    storedPositions.delete(oldestKey);
  }
};

const ScrollToTop = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const hasHandledFirstLocationRef = React.useRef(false);

  React.useEffect(() => {
    if (!("scrollRestoration" in window.history)) return;

    const previousScrollRestoration = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";

    return () => {
      window.history.scrollRestoration = previousScrollRestoration;
    };
  }, []);

  React.useLayoutEffect(() => {
    const navigationState = location.state as ScrollRestorationLocationState | null;
    const requestedKey = navigationState?.restoreScrollFromKey;
    const restorationKey =
      requestedKey ??
      (hasHandledFirstLocationRef.current && navigationType === "POP"
        ? location.key
        : undefined);
    const storedPosition = restorationKey
      ? storedPositions.get(restorationKey)
      : undefined;
    const cancelScroll = storedPosition
      ? scrollPageToPosition(storedPosition)
      : scrollPageToTop();

    hasHandledFirstLocationRef.current = true;

    return () => {
      cancelScroll();
      storePosition(location.key);
    };
  }, [
    location.hash,
    location.key,
    location.pathname,
    location.search,
    location.state,
    navigationType,
  ]);

  return null;
};

export default ScrollToTop;
